import React from "react";
import {
  HttpLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { ConfigProvider, theme } from "antd";
import { BrowserRouter as Router } from "react-router-dom";
import Login from "./components/Login";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import PageLoader from "./components/PageLoader";
import { migrationApiUrl } from "./lib/env";
import { useSession } from "./lib/auth-client";
import "./App.css";

const httpLink = new HttpLink({
  uri: migrationApiUrl,
  credentials: "include",
});

// reload page to redirect to login page when session is invalidated
let handlingUnauthenticated = false;
const errorLink = onError(({ graphQLErrors }) => {
  const unauthenticated = graphQLErrors?.some(
    (err) => err.extensions?.code === "UNAUTHENTICATED",
  );
  if (unauthenticated && !handlingUnauthenticated) {
    handlingUnauthenticated = true;
    window.location.reload();
  }
});

export const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
});

console.log("Using API URL:", migrationApiUrl);

const App = () => {
  const isDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const { data: sessionData, isPending } = useSession();

  const authDisabled = import.meta.env.VITE_DISABLE_AUTH === "true";

  const user = authDisabled ? { name: "Dev User" } : sessionData?.user;
  const showLayout = authDisabled || Boolean(sessionData?.session);

  const renderContent = () => {
    if (!authDisabled && isPending) {
      return (
        <PageLoader
          style={{ display: "flex", justifyContent: "center", marginTop: 120 }}
        />
      );
    }
    if (!showLayout) {
      return <Login />;
    }
    return (
      <ApolloProvider client={client}>
        <Router>
          <AuthenticatedLayout user={user} isDarkMode={isDarkMode} />
        </Router>
      </ApolloProvider>
    );
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      {renderContent()}
    </ConfigProvider>
  );
};

export default App;
